import React, { useState, useEffect } from "react";
import { Edit, Trash2, Plus, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./Bank.css";

const url = process.env.REACT_APP_BACKEND_URL;

const BankList = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const navigate = useNavigate();

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const endpoint = showInactive ? `${url}/api/bank/all` : `${url}/api/bank`;
      const res = await axios.get(endpoint);
      setBanks(res.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch banks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, [showInactive]);

  const handleSoftDelete = async (id) => {
    if (window.confirm("Are you sure you want to deactivate this bank?")) {
      try {
        const res = await axios.delete(`${url}/api/bank/${id}`);
        toast.success(res.data.message || "Bank deactivated successfully");
        fetchBanks();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to deactivate bank");
      }
    }
  };

  const handleRestore = async (id) => {
    if (window.confirm("Are you sure you want to activate this bank?")) {
      try {
        const res = await axios.put(`${url}/api/bank/${id}/restore`);
        toast.success(res.data.message || "Bank activated successfully");
        fetchBanks();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to activate bank");
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) return <div className="loading">Loading banks...</div>;

  return (
    <div className="bank-container">
      <div className="header">
        <h2>Banks List</h2>
        <div className="header-actions">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`toggle-button ${showInactive ? 'active' : ''}`}
          >
            {showInactive ? 'Showing All' : 'Show Active Only'}
          </button>
          <button
            onClick={() => navigate("/AddBank")}
            className="add-button"
          >
            <Plus size={16} /> Add Bank
          </button>
        </div>
      </div>

      <table className="bank-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Bank Name</th>
            <th>Phone Number</th>
            <th>Deposit</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {banks.length === 0 ? (
            <tr>
              <td colSpan="6" className="no-data">
                No banks found
              </td>
            </tr>
          ) : (
            banks.map((bank) => (
              <tr key={bank._id} className={bank.status === 'inactive' ? 'inactive-row' : ''}>
                <td>{bank.no}</td>
                <td>{bank.bankname}</td>
                <td>{bank.bankaccountno}</td>
                <td>{formatCurrency(bank.deposit)}</td>
                <td>
                  <span className={`status-badge ${bank.status}`}>
                    {bank.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="actions">
                    {bank.status === 'active' ? (
                      <>
                        <button
                          onClick={() => navigate(`/bank/edit/${bank._id}`)}
                          className="edit"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleSoftDelete(bank._id)}
                          className="delete"
                          title="Deactivate"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleRestore(bank._id)}
                        className="restore-btn"
                        title="Activate"
                      >
                        <RotateCcw size={16} /> Activate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default BankList;